import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Inbox, Mail, MailOpen, Reply, Loader2, User, Send, CheckCircle2 } from "lucide-react";

function fmtDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function ContactInbox() {
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [replyDraft, setReplyDraft] = useState({});
  const [replying, setReplying] = useState({});

  const load = () => api.get("/contacts").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const sendReply = async (c) => {
    const message = (replyDraft[c.id] ?? "").trim();
    if (!message) { toast.error("Write a reply first."); return; }
    setReplying((r) => ({ ...r, [c.id]: true }));
    try {
      const { data } = await api.post(`/contacts/${c.id}/reply`, { message });
      setItems((prev) => prev.map((x) => (x.id === c.id ? { ...x, read: true, replied_at: new Date().toISOString(), replies: data.replies } : x)));
      setReplyDraft((d) => { const n = { ...d }; delete n[c.id]; return n; });
      toast.success(`Reply sent to ${c.email}.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setReplying((r) => ({ ...r, [c.id]: false })); }
  };

  const toggleOpen = async (c) => {
    const next = openId === c.id ? null : c.id;
    setOpenId(next);
    if (next && !c.read) {
      setItems((prev) => prev.map((x) => (x.id === c.id ? { ...x, read: true } : x)));
      try { await api.put(`/contacts/${c.id}/read`, { read: true }); } catch { /* non-blocking */ }
    }
  };

  const markUnread = async (e, c) => {
    e.stopPropagation();
    setItems((prev) => prev.map((x) => (x.id === c.id ? { ...x, read: false } : x)));
    try { await api.put(`/contacts/${c.id}/read`, { read: false }); } catch { /* non-blocking */ }
  };

  const mailto = (c) => {
    const subject = `Re: ${c.subject || "Your enquiry to Noventra Academic Press"}`;
    const body = `\n\n---\nOn ${fmtDate(c.created_at)}, ${c.name} wrote:\n${c.message}`;
    return `mailto:${c.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (!items) return <div className="py-16 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const unread = items.filter((c) => !c.read).length;

  return (
    <div data-testid="contact-inbox">
      <h1 className="font-serif font-bold text-2xl text-slate-900 dark:text-slate-100 mb-1 flex items-center gap-2">
        <Inbox className="w-6 h-6 text-amber-600" /> Contact Inbox
        {unread > 0 && <Badge className="bg-amber-500 text-white border-0" data-testid="inbox-unread-count">{unread} new</Badge>}
      </h1>
      <p className="text-slate-500 text-sm mb-5">Enquiries sent through the public Contact form. Click one to read it and reply.</p>

      {items.length === 0 ? (
        <div className="bg-card rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-12 text-center text-slate-400">
          No enquiries yet.
        </div>
      ) : (
        <div className="space-y-3 max-w-3xl">
          {items.map((c) => {
            const isOpen = openId === c.id;
            return (
              <div
                key={c.id}
                data-testid={`inbox-item-${c.id}`}
                onClick={() => toggleOpen(c)}
                className={`cursor-pointer bg-card rounded-xl border transition-all ${c.read ? "border-slate-200 dark:border-slate-800" : "border-amber-300 dark:border-amber-600/50 bg-amber-50/40 dark:bg-amber-950/10"}`}
              >
                <div className="flex items-start gap-3 p-4">
                  <span className={`grid place-items-center w-10 h-10 rounded-full shrink-0 ${c.read ? "bg-slate-100 dark:bg-slate-800 text-slate-400" : "bg-amber-100 dark:bg-amber-900/40 text-amber-600"}`}>
                    {c.read ? <MailOpen className="w-5 h-5" /> : <Mail className="w-5 h-5" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`truncate ${c.read ? "font-medium" : "font-bold"} text-slate-900 dark:text-slate-100`}>{c.subject || "General enquiry"}</span>
                      {!c.read && <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />}
                      <span className="text-xs text-slate-400 ml-auto">{fmtDate(c.created_at)}</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-0.5 flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5" /> {c.name} · <span className="text-primary dark:text-sky-400">{c.email}</span>
                    </p>
                    {!isOpen && <p className="text-sm text-slate-500 mt-1 line-clamp-1">{c.message}</p>}
                  </div>
                </div>

                {isOpen && (
                  <div className="px-4 pb-4 pl-[68px] space-y-3" onClick={(e) => e.stopPropagation()}>
                    <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed border-l-2 border-slate-200 dark:border-slate-700 pl-3">{c.message}</p>

                    {(c.replies || []).length > 0 && (
                      <div className="space-y-2" data-testid={`inbox-replies-${c.id}`}>
                        {c.replies.map((r, i) => (
                          <div key={i} className="rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/40 p-3">
                            <p className="text-xs text-emerald-700 dark:text-emerald-400 font-medium flex items-center gap-1.5 mb-1"><CheckCircle2 className="w-3.5 h-3.5" /> Replied {r.at ? `· ${fmtDate(r.at)}` : ""}{r.by ? ` · ${r.by}` : ""}</p>
                            <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{r.message}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3 space-y-2.5" data-testid={`inbox-reply-box-${c.id}`}>
                      <Textarea
                        rows={4}
                        value={replyDraft[c.id] ?? ""}
                        onChange={(e) => setReplyDraft((d) => ({ ...d, [c.id]: e.target.value }))}
                        placeholder={`Write your reply to ${c.name}…`}
                        className="text-sm"
                        data-testid={`inbox-reply-text-${c.id}`}
                      />
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button size="sm" disabled={replying[c.id]} onClick={() => sendReply(c)} className="rounded-full gap-1.5 bg-amber-500 hover:bg-amber-600 text-white" data-testid={`inbox-send-reply-${c.id}`}>
                          {replying[c.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send reply
                        </Button>
                        <Button asChild size="sm" variant="outline" className="rounded-full gap-1.5" data-testid={`inbox-reply-${c.id}`}>
                          <a href={mailto(c)}><Reply className="w-4 h-4" /> Open in email app</a>
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={(e) => markUnread(e, c)} data-testid={`inbox-unread-${c.id}`}>
                          <Mail className="w-4 h-4" /> Mark unread
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
